import React, {useEffect, useState, useRef, useMemo} from "react";
import {
  Typography,
  Box,
  Alert,
  CircularProgress,
  Button,
  Card,
  CardContent,
  Grid,
} from "@mui/material";
import {useNavigate} from "react-router-dom";
import {useServiceRequestQueries, useNotification, Bid} from "shared-lib";
import {useVendorContext} from "../context/VendorContextProvider";
import {ServiceRequestCard, ServiceRequestFilters, StatusFilter} from "../components/ServiceRequests";
import {getDisplayStatus} from "../utils/serviceRequestUtils";

const ServiceRequests: React.FC = () => {
  const navigate = useNavigate();
  const {useAvailableServiceRequests, useVendorBids, useVendorServiceRequests} = useServiceRequestQueries();
  const {currentVendor, loading: vendorLoading} = useVendorContext();
  const {showNotification} = useNotification();

  // Store showNotification in a ref to prevent it from causing re-renders
  const showNotificationRef = useRef(showNotification);
  // Update ref when showNotification changes
  useEffect(() => {
    showNotificationRef.current = showNotification;
  }, [showNotification]);

  // Filter state
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("no-bid");
  const [serviceTypeFilter, setServiceTypeFilter] = useState("all");

  // Fetch active service requests
  const {
    data: activeRequests = [],
    isLoading: activeRequestsLoading,
    error: activeRequestsError,
    isError: isActiveRequestsError,
  } = useAvailableServiceRequests();

  // Fetch requests where this vendor has bids
  const {
    data: vendorRequests = [],
    isLoading: vendorRequestsLoading,
    error: vendorRequestsError,
    isError: isVendorRequestsError,
  } = useVendorServiceRequests(currentVendor?.id || "");

  const {data: vendorBids = [], isLoading: bidsLoading} = useVendorBids(currentVendor?.id || "");

  // Combine active requests and vendor requests (removing duplicates)
  const serviceRequests = useMemo(() => {
    const combinedRequests = [...activeRequests];

    // Add vendor requests that aren't already in active requests
    vendorRequests.forEach(vendorRequest => {
      if (!combinedRequests.some(req => req.id === vendorRequest.id)) {
        combinedRequests.push(vendorRequest);
      }
    });

    return combinedRequests;
  }, [activeRequests, vendorRequests]);

  const isLoading = activeRequestsLoading || vendorRequestsLoading || vendorLoading || bidsLoading;
  const isError = isActiveRequestsError || isVendorRequestsError;

  // Show error notification if there's an error
  useEffect(() => {
    if (isError) {
      showNotificationRef.current({
        message: "Error loading service requests. Please try again.",
        severity: "error",
      });
    }
  }, [isError]);

  // Show notification if no vendor is selected
  useEffect(() => {
    if (!isLoading && !currentVendor) {
      showNotificationRef.current({
        message: "Please select a vendor company from the menu.",
        severity: "warning",
      });
    }
  }, [isLoading, currentVendor]);

  const handleViewRequest = (requestId: string) => {
    navigate(`/requests/${requestId}`);
  };

  // Function to get bid status for a request
  const getBidStatus = (requestId: string) => {
    if (!currentVendor) return null;
    return vendorBids.find(bid => bid.requestId === requestId) as Bid | null;
  };

  // Filter requests based on the current filters
  const getFilteredRequests = () => {
    const now = new Date();

    return serviceRequests.filter(request => {
      const bidStatus = getBidStatus(request.id);
      const currentStatus = getDisplayStatus(bidStatus);

      // Skip removed requests
      if (request.status === "removed") {
        return false;
      }

      // Skip requests with rejected bids
      if (currentStatus === "rejected") {
        return false;
      }

      // For 'no-bid' requests, check if bidding end date has passed
      if (currentStatus === "no-bid") {
        const biddingEndDate = request.biddingEndDate;

        // If bidding end date has passed, don't show this request in the list
        if (biddingEndDate < now) {
          return false;
        }
      }

      // If "all" filter is selected, show all remaining requests
      if (statusFilter === "all") {
        return true;
      }
      // Status filter for other statuses
      else {
        // If filter doesn't match current status, exclude it
        if (currentStatus !== statusFilter) return false;
      }

      // Service type filter
      if (serviceTypeFilter !== "all") {
        // Use serviceName for the new single service model
        if (request.serviceName !== serviceTypeFilter) return false;
      }

      return true;
    });
  };

  // Get unique service types for filter dropdown
  const getServiceTypes = () => {
    const types = new Set<string>(["all"]);

    serviceRequests.forEach(request => {
      if (request.serviceName) types.add(request.serviceName);
    });

    return Array.from(types).sort();
  };

  // Count requests by status for badges
  const getStatusCounts = () => {
    const counts = {
      all: 0,
      "no-bid": 0,
      pending: 0,
      accepted: 0,
      ignored: 0,
    };

    const now = new Date();

    // Count statuses
    serviceRequests.forEach(request => {
      // Skip removed requests from counts
      if (request.status === "removed") {
        return;
      }

      const bidStatus = getBidStatus(request.id);
      const currentStatus = getDisplayStatus(bidStatus);

      // Skip rejected bids from counts
      if (currentStatus === "rejected") {
        return;
      }

      // For 'no-bid' requests, check if bidding end date has passed
      if (currentStatus === "no-bid") {
        const biddingEndDate =
          request.biddingEndDate instanceof Date ? request.biddingEndDate : new Date(request.biddingEndDate);

        // If bidding end date has passed, don't count it
        if (biddingEndDate < now) {
          return;
        }
      }

      // Count all valid requests in the "all" category
      counts.all++;

      if (!bidStatus) {
        counts["no-bid"]++;
      } else if (currentStatus === "pending") {
        counts.pending++;
      } else if (currentStatus === "accepted") {
        counts.accepted++;
      } else if (currentStatus === "ignored") {
        counts.ignored++;
      }
    });

    return counts;
  };

  const statusCounts = getStatusCounts();
  const filteredRequests = getFilteredRequests();
  const serviceTypes = getServiceTypes();

  if (isLoading) {
    return (
      <Box sx={{display: "flex", justifyContent: "center", my: 4}}>
        <CircularProgress />
      </Box>
    );
  }

  if (!currentVendor) {
    return (
      <Box sx={{textAlign: "center", p: 4}}>
        <Typography variant="h6">No vendor company selected</Typography>
        <Typography variant="body1" sx={{mt: 2}}>
          Please select a vendor company from the menu.
        </Typography>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{my: 4}}>
        <Alert severity="error" sx={{mb: 2}}>
          There was an error loading the service requests.
        </Alert>
        <Button variant="outlined" onClick={() => window.location.reload()}>
          Try Again
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{mb: 4}}>
      <ServiceRequestFilters
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        serviceTypeFilter={serviceTypeFilter}
        setServiceTypeFilter={setServiceTypeFilter}
        statusCounts={statusCounts}
        serviceTypes={serviceTypes}
      />

      {filteredRequests.length === 0 ? (
        <Card
          sx={{
            textAlign: "center",
            p: 3,
            borderRadius: "16px",
            border: "1px solid rgba(16, 29, 59, 0.2)",
            boxShadow: "0px 8px 32px -4px rgba(16, 29, 59, 0.1)",
          }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{color: "#101D3B", fontWeight: 700}}>
              No service requests match your filters
            </Typography>
            <Typography variant="body2" sx={{color: "rgba(16, 29, 59, 0.6)", mb: 2}}>
              Try adjusting your filter settings or check back later for new requests.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => {
                setStatusFilter("all");
                setServiceTypeFilter("all");
              }}
              sx={{
                borderColor: "#00AB9B",
                color: "#00AB9B",
                borderRadius: "5px",
                "&:hover": {
                  borderColor: "#00AB9B",
                  backgroundColor: "rgba(0, 171, 155, 0.04)",
                },
              }}>
              Clear Filters
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {filteredRequests.map(request => {
            const bidStatus = getBidStatus(request.id);

            return (
              <Grid item xs={12} md={6} lg={4} key={request.id}>
                <ServiceRequestCard request={request} bidStatus={bidStatus} onViewRequest={handleViewRequest} />
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};

export default ServiceRequests;
